import type { ZoriModuleManifest } from "../../app/module-manifest";

export const PLATFORM_OVERVIEW_PATH = "/platform";
export const PLATFORM_JOBS_PATH = "/platform/jobs";
export const PLATFORM_DATA_PATH = "/platform/data";
export const PLATFORM_DATABASE_PATH = "/platform/database";
export const PLATFORM_FUNCTIONS_PATH = "/platform/functions";
export const PLATFORM_SERVICES_PATH = "/platform/services";
export const PLATFORM_RESOURCES_PATH = "/platform/resources";
export const PLATFORM_SCHEDULES_PATH = "/platform/schedules";
export const PLATFORM_SECRETS_PATH = "/platform/secrets";
export const PLATFORM_WEBHOOKS_PATH = "/platform/webhooks";
export const PLATFORM_EVENTS_PATH = "/platform/events";
export const PLATFORM_CONFIG_PATH = "/platform/config";
export const PLATFORM_NOTIFICATION_INFRA_PATH = "/platform/notification-infra";
export const PLATFORM_API_PATH = "/platform/api";
export const PLATFORM_INTEGRATIONS_PATH = "/integrations";
export const PLATFORM_INTEGRACIONES_ALIAS_PATH = "/integraciones";
export const PLATFORM_IDENTITIES_ALIAS_PATH = "/identities";
export const PLATFORM_SESSIONS_PATH = "/sessions";
export const PLATFORM_CONVERSACIONES_ALIAS_PATH = "/conversaciones";
export const PLATFORM_CONVERSATIONS_ALIAS_PATH = "/conversations";
export const PLATFORM_FBA_PATH = "/fba";
export const PLATFORM_FBA_D1_ALIAS_PATH = "/fba-d1";
export const PLATFORM_FBA_D1_RUN_ALIAS_PATH = "/fba-d1/runs/:run";
export const PLATFORM_ACTIONS_PATH = "/actions";
export const PLATFORM_STORAGE_PATH = "/storage";
export const PLATFORM_RUNTIME_PATH = "/runtime";
export const PLATFORM_MIGRATION_PATH = "/platform/migration";
export const PLATFORM_EXECUTIONS_PATH = "/platform/executions";
export const PLATFORM_EXECUTION_DETAIL_PATH = "/platform/executions/:kind/:id";
export const PLATFORM_LOGS_PATH = "/platform/logs";
export const PLATFORM_METRICS_PATH = "/platform/metrics";
export const PLATFORM_HEALTH_PATH = "/platform/health";
export const PLATFORM_SEARCH_PATH = "/platform/search";

/**
 * The Platform module consolidates the operational v2 surfaces:
 * `/integrations` replaces Lit `identities` (provider connections,
 * capabilities, syncs, webhook subscriptions — OAuth only for providers
 * whose kind supports it); `/sessions` merges the two Lit conversation
 * browsers (`sessions` + `conversations`); `/fba` replaces Lit `fba-d1`
 * (definitions, runs, node/artifact/audit detail, start + cancel);
 * `/actions`, `/storage`, `/runtime` are greenfield over mounted contracts
 * (action registry + executions, storage buckets/objects/lifecycle,
 * runtime bindings + approvals). Legacy Lit paths stay as hidden aliases
 * that redirect to the canonical React paths.
 *
 * Capability gates mirror the server: `integrations:read` for
 * integrations, `conversations:read` for sessions and runtime, `fba:read`
 * for process operations, `actions:read` for the registry, `storage:read`
 * for storage. The module-level gate stays empty because these
 * permissions are disjoint across roles (FBA Viewer holds `fba:read`
 * only) — per-route gates govern.
 */
export const platformModuleManifest: ZoriModuleManifest = {
  id: "platform",
  labelKey: "zori.modules.platform",
  requiredCapabilities: [],
  routes: [
    {
      id: "platform-overview",
      path: PLATFORM_OVERVIEW_PATH,
      labelKey: "zori.platform.overview.page",
      navLabelKey: "zori.nav.overview",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "dashboard",
    },
    {
      id: "platform-jobs",
      path: PLATFORM_JOBS_PATH,
      labelKey: "zori.platform.jobs.page",
      requiredCapabilities: ["jobs:read"],
      navGroup: "platform",
      icon: "run",
    },
    {
      id: "platform-data",
      path: PLATFORM_DATA_PATH,
      labelKey: "zori.platform.data.page",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "database",
    },
    {
      // P8.7 — raw PostgreSQL administration, kept out of the product Data
      // surface: catalog/inspector/browser/migrations/maintenance.
      id: "platform-database",
      path: PLATFORM_DATABASE_PATH,
      labelKey: "zori.platform.database.page",
      navLabelKey: "zori.nav.databaseAdmin",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "tool",
    },
    {
      id: "platform-functions",
      path: PLATFORM_FUNCTIONS_PATH,
      labelKey: "zori.platform.functions.page",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "terminal",
    },
    {
      id: "platform-services",
      path: PLATFORM_SERVICES_PATH,
      labelKey: "zori.platform.services.page",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "server",
    },
    {
      id: "platform-resources",
      path: PLATFORM_RESOURCES_PATH,
      labelKey: "zori.platform.resources.page",
      navLabelKey: "zori.nav.resources",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "server",
    },
    {
      id: "platform-schedules",
      path: PLATFORM_SCHEDULES_PATH,
      labelKey: "zori.platform.schedules.page",
      requiredCapabilities: ["schedules:read"],
      navGroup: "platform",
      icon: "history",
    },
    {
      id: "platform-secrets",
      path: PLATFORM_SECRETS_PATH,
      labelKey: "zori.platform.secrets.page",
      requiredCapabilities: ["secrets:read"],
      navGroup: "platform",
      icon: "shield",
    },
    {
      id: "platform-webhooks",
      path: PLATFORM_WEBHOOKS_PATH,
      labelKey: "zori.platform.webhooks.page",
      requiredCapabilities: ["webhooks:read"],
      navGroup: "platform",
      icon: "bolt",
    },
    {
      id: "platform-events",
      path: PLATFORM_EVENTS_PATH,
      labelKey: "zori.platform.events.page",
      requiredCapabilities: ["events:read"],
      navGroup: "platform",
      icon: "bolt",
    },
    {
      id: "platform-config",
      path: PLATFORM_CONFIG_PATH,
      labelKey: "zori.platform.config.page",
      requiredCapabilities: ["config:read"],
      navGroup: "platform",
      icon: "settings",
    },
    {
      id: "platform-notification-infra",
      path: PLATFORM_NOTIFICATION_INFRA_PATH,
      labelKey: "zori.platform.notificationInfra.page",
      requiredCapabilities: ["channels:read"],
      navGroup: "platform",
      icon: "bell",
    },
    {
      id: "platform-api",
      path: PLATFORM_API_PATH,
      labelKey: "zori.platform.api.page",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "book",
    },
    {
      id: "integrations",
      path: PLATFORM_INTEGRATIONS_PATH,
      labelKey: "zori.platform.integrations.page",
      requiredCapabilities: ["integrations:read"],
      navGroup: "platform",
      icon: "plug",
    },
    {
      id: "integraciones-alias",
      path: PLATFORM_INTEGRACIONES_ALIAS_PATH,
      labelKey: "zori.platform.integrations.page",
      canonicalRouteId: "platform:integrations",
      requiredCapabilities: ["integrations:read"],
      navHidden: true,
    },
    {
      id: "identities-alias",
      path: PLATFORM_IDENTITIES_ALIAS_PATH,
      labelKey: "zori.platform.integrations.page",
      canonicalRouteId: "platform:integrations",
      requiredCapabilities: ["integrations:read"],
      navHidden: true,
    },
    {
      id: "sessions",
      path: PLATFORM_SESSIONS_PATH,
      labelKey: "zori.platform.sessions.page",
      requiredCapabilities: ["conversations:read"],
      navGroup: "platform",
      icon: "threads",
    },
    {
      id: "conversaciones-alias",
      path: PLATFORM_CONVERSACIONES_ALIAS_PATH,
      labelKey: "zori.platform.sessions.page",
      canonicalRouteId: "platform:sessions",
      requiredCapabilities: ["conversations:read"],
      navHidden: true,
    },
    {
      id: "conversations-alias",
      path: PLATFORM_CONVERSATIONS_ALIAS_PATH,
      labelKey: "zori.platform.sessions.page",
      canonicalRouteId: "platform:sessions",
      requiredCapabilities: ["conversations:read"],
      navHidden: true,
    },
    {
      id: "fba",
      path: PLATFORM_FBA_PATH,
      labelKey: "zori.platform.fba.page",
      requiredCapabilities: ["fba:read"],
      navGroup: "platform",
      icon: "run",
    },
    {
      id: "fba-d1-alias",
      path: PLATFORM_FBA_D1_ALIAS_PATH,
      labelKey: "zori.platform.fba.page",
      canonicalRouteId: "platform:fba",
      requiredCapabilities: ["fba:read"],
      navHidden: true,
    },
    {
      // Lit-era FBA run deep links: `/fba-d1/runs/<id>` canonicalizes to
      // `/fba?run=<id>` — the React page expands the referenced run.
      id: "fba-run-alias",
      path: PLATFORM_FBA_D1_RUN_ALIAS_PATH,
      labelKey: "zori.platform.fba.page",
      canonicalRouteId: "platform:fba",
      context: { resourceType: "run", idParam: "run" },
      requiredCapabilities: ["fba:read"],
      navHidden: true,
    },
    {
      id: "actions",
      path: PLATFORM_ACTIONS_PATH,
      labelKey: "zori.platform.actions.page",
      requiredCapabilities: ["actions:read"],
      navGroup: "platform",
      icon: "bolt",
    },
    {
      id: "storage",
      path: PLATFORM_STORAGE_PATH,
      labelKey: "zori.platform.storage.page",
      requiredCapabilities: ["storage:read"],
      navGroup: "platform",
      icon: "archive",
    },
    {
      id: "runtime",
      path: PLATFORM_RUNTIME_PATH,
      labelKey: "zori.platform.runtime.page",
      requiredCapabilities: ["conversations:read"],
      navGroup: "platform",
      icon: "server",
    },
    {
      id: "platform-observability-executions",
      path: PLATFORM_EXECUTIONS_PATH,
      labelKey: "zori.platform.observability.executions.page",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "run",
    },
    {
      id: "platform-observability-execution-detail",
      path: PLATFORM_EXECUTION_DETAIL_PATH,
      labelKey: "zori.platform.observability.executionDetail.page",
      requiredCapabilities: ["platform:read"],
      navHidden: true,
      canonicalRouteId: "platform-observability-executions",
    },
    {
      id: "platform-observability-logs",
      path: PLATFORM_LOGS_PATH,
      labelKey: "zori.platform.observability.logs.page",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "terminal",
    },
    {
      id: "platform-search",
      path: PLATFORM_SEARCH_PATH,
      labelKey: "zori.platform.search.page",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "search",
    },
    {
      id: "platform-observability-metrics",
      path: PLATFORM_METRICS_PATH,
      labelKey: "zori.platform.observability.metrics.page",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "dashboard",
    },
    {
      id: "platform-observability-health",
      path: PLATFORM_HEALTH_PATH,
      labelKey: "zori.platform.observability.health.page",
      requiredCapabilities: ["platform:read"],
      navGroup: "platform",
      icon: "dashboard",
    },
    {
      id: "platform-migration",
      path: PLATFORM_MIGRATION_PATH,
      labelKey: "zori.platform.migration.page",
      requiredCapabilities: ["migration:read"],
      navGroup: "platform",
      icon: "bolt",
    },
  ],
};
